import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CalendarDays, FileText, MessageSquare, Paperclip, RefreshCw, Search, Send, Truck } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import styles from './ClientPickupRequestsPage.module.css';

const unwrapList = (data) => Array.isArray(data) ? data : (data?.results || []);

const formatDate = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getRequestNumber = (item) => item?.request_number || '-';
const getCustomerName = (item) => item?.customer_name || item?.customer?.name || '-';
const getProjectName = (item) => item?.project_name || item?.project?.name || '-';

const emptyAssignForm = {
  pickup_transport_company: '',
  pickup_track_number: '',
  pickup_date: '',
};

const statuses = [
  { value: 'requested', label: 'Ожидают логиста' },
  { value: 'assigned', label: 'ТК назначена' },
  { value: '', label: 'Все' },
];

const ClientPickupRequestsPage = () => {
  const [requests, setRequests] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [status, setStatus] = useState('requested');
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [assignForm, setAssignForm] = useState(emptyAssignForm);
  const [ttnFile, setTtnFile] = useState(null);
  const [isTtnDragActive, setIsTtnDragActive] = useState(false);
  const [isTicketFormOpen, setIsTicketFormOpen] = useState(false);
  const [ticketMessage, setTicketMessage] = useState('');
  const [isTicketSending, setIsTicketSending] = useState(false);
  const ttnFileInputRef = useRef(null);

  const resetAssignForm = useCallback((request = null) => {
    setAssignForm({
      pickup_transport_company: request?.pickup_transport_company || '',
      pickup_track_number: request?.pickup_track_number || '',
      pickup_date: request?.pickup_date || '',
    });
    setTtnFile(null);
    if (ttnFileInputRef.current) {
      ttnFileInputRef.current.value = '';
    }
    setIsTtnDragActive(false);
    setIsTicketFormOpen(false);
    setTicketMessage('');
  }, []);

  const fetchRequests = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/repairs/client-requests/pickup-requests/', {
        params: {
          pickup_status: status || undefined,
          search: search.trim() || undefined,
          page_size: 100,
        },
      });
      const items = unwrapList(response.data);
      setRequests(items);
      setSelectedId(prevId => {
        if (prevId && items.some(item => Number(item.id) === Number(prevId))) {
          return prevId;
        }
        return items[0]?.id || null;
      });
      if (items.length === 0) {
        setSelectedRequest(null);
      }
    } catch (error) {
      console.error('Ошибка загрузки клиентских заявок на забор:', error);
      toast.error('Не удалось загрузить заявки на забор');
    } finally {
      setIsLoading(false);
    }
  }, [search, status]);

  const fetchDetail = useCallback(async (id) => {
    if (!id) return;
    setIsDetailLoading(true);
    try {
      const response = await api.get(`/repairs/client-requests/${id}/`);
      setSelectedRequest(response.data);
      resetAssignForm(response.data);
    } catch (error) {
      console.error('Ошибка загрузки заявки на забор:', error);
      toast.error('Не удалось открыть заявку');
    } finally {
      setIsDetailLoading(false);
    }
  }, [resetAssignForm]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  useEffect(() => {
    if (selectedId) {
      fetchDetail(selectedId);
    }
  }, [fetchDetail, selectedId]);

  useEffect(() => {
    const handleOpenPickup = (event) => {
      const requestId = event?.detail?.repair_request_id || event?.detail?.repairRequestId;
      if (!requestId) return;
      setStatus('');
      setSelectedId(Number(requestId));
    };
    const pendingId = localStorage.getItem('client_pickup_open_id');
    if (pendingId) {
      localStorage.removeItem('client_pickup_open_id');
      setStatus('');
      setSelectedId(Number(pendingId));
    }
    window.addEventListener('crm-open-client-pickup', handleOpenPickup);
    return () => window.removeEventListener('crm-open-client-pickup', handleOpenPickup);
  }, []);

  const handleAssignChange = (event) => {
    const { name, value } = event.target;
    setAssignForm(prev => ({ ...prev, [name]: value }));
  };

  const handleTtnDrop = (event) => {
    event.preventDefault();
    setIsTtnDragActive(false);
    const droppedFile = event.dataTransfer?.files?.[0];
    if (droppedFile) {
      setTtnFile(droppedFile);
    }
  };

  const createPickupTicket = async () => {
    if (!selectedRequest) return;
    if (!ticketMessage.trim()) {
      toast.info('Введите вопрос клиенту');
      return;
    }
    const formData = new FormData();
    formData.append('repair_request_id', selectedRequest.id);
    formData.append('subject', `Вопрос по забору ${selectedRequest.request_number}`);
    formData.append('content', ticketMessage.trim());

    try {
      setIsTicketSending(true);
      await api.post('/repairs/client-tickets/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setTicketMessage('');
      setIsTicketFormOpen(false);
      toast.success('Тикет клиенту создан');
    } catch (error) {
      console.error('Ошибка создания тикета по забору:', error);
      toast.error(error.response?.data?.error || 'Не удалось создать тикет');
    } finally {
      setIsTicketSending(false);
    }
  };

  const assignPickup = async (event) => {
    event.preventDefault();
    if (!selectedRequest) return;
    if (!assignForm.pickup_transport_company.trim() || !assignForm.pickup_track_number.trim() || !assignForm.pickup_date) {
      toast.info('Укажите ТК, номер ТТН и дату забора');
      return;
    }
    if (!ttnFile && !selectedRequest.pickup_ttn_file_url) {
      toast.info('Приложите ТТН для клиента');
      return;
    }

    const formData = new FormData();
    formData.append('pickup_transport_company', assignForm.pickup_transport_company.trim());
    formData.append('pickup_track_number', assignForm.pickup_track_number.trim());
    formData.append('pickup_date', assignForm.pickup_date);
    if (ttnFile) {
      formData.append('pickup_ttn_file', ttnFile);
    }

    try {
      setIsAssigning(true);
      const response = await api.post(`/repairs/client-requests/${selectedRequest.id}/assign-pickup/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSelectedRequest(response.data);
      resetAssignForm(response.data);
      await fetchRequests();
      toast.success('Транспортная компания назначена, клиент уведомлен');
    } catch (error) {
      console.error('Ошибка назначения ТК:', error);
      toast.error(error.response?.data?.error || 'Не удалось назначить ТК');
    } finally {
      setIsAssigning(false);
    }
  };

  const selectedStatus = selectedRequest?.client_pickup_status_display || selectedRequest?.status_display || '-';

  return (
    <div className={styles.page}>
      <section className={styles.listPane}>
        <div className={styles.controls}>
          <div className={styles.searchBox}>
            <Search size={18} />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Поиск по заявке, заказчику, адресу или ТТН"
            />
          </div>
          <button type="button" className={styles.refreshButton} onClick={fetchRequests}>
            <RefreshCw size={17} />
            Обновить
          </button>
          <div className={styles.statusTabs}>
            {statuses.map(item => (
              <button
                key={item.value || 'all'}
                type="button"
                className={status === item.value ? styles.statusActive : styles.statusButton}
                onClick={() => {
                  setStatus(item.value);
                  setSelectedId(null);
                  setSelectedRequest(null);
                }}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        {isLoading ? (
          <div className={styles.loadingState}>
            <div className={styles.spinner} />
            <span>Загрузка заявок...</span>
          </div>
        ) : requests.length === 0 ? (
          <div className={styles.emptyState}>
            <Truck size={42} />
            <h3>Заявок на забор нет</h3>
            <p>Клиентские заявки появятся здесь после отправки формы клиентом.</p>
          </div>
        ) : (
          <div className={styles.requestList}>
            {requests.map(item => (
              <button
                key={item.id}
                type="button"
                className={`${styles.requestCard} ${selectedId === item.id ? styles.requestCardActive : ''}`}
                onClick={() => setSelectedId(item.id)}
              >
                <div className={styles.cardHead}>
                  <strong>{getRequestNumber(item)}</strong>
                  <span>{item.client_pickup_status_display || item.status_display || '-'}</span>
                </div>
                <p>{getCustomerName(item)}</p>
                <em>{item.pickup_address || 'Адрес не указан'}</em>
                <time>{formatDate(item.created_at)}</time>
              </button>
            ))}
          </div>
        )}
      </section>

      <section className={styles.detailPane}>
        {isDetailLoading ? (
          <div className={styles.loadingState}>
            <div className={styles.spinner} />
            <span>Открываем заявку...</span>
          </div>
        ) : !selectedRequest ? (
          <div className={styles.emptyState}>
            <FileText size={42} />
            <h3>Выберите заявку</h3>
            <p>Детали забора и форма назначения ТК появятся справа.</p>
          </div>
        ) : (
          <>
            <div className={styles.detailHeader}>
              <div>
                <span>{getProjectName(selectedRequest)}</span>
                <h2>{selectedRequest.request_number}</h2>
                <p>{getCustomerName(selectedRequest)}</p>
              </div>
              <em>{selectedStatus}</em>
            </div>

            <form className={styles.assignForm} onSubmit={assignPickup}>
              <label>
                <span>Транспортная компания</span>
                <input
                  name="pickup_transport_company"
                  value={assignForm.pickup_transport_company}
                  onChange={handleAssignChange}
                  placeholder="Например, СДЭК"
                  required
                />
              </label>
              <label>
                <span>Номер ТТН</span>
                <input
                  name="pickup_track_number"
                  value={assignForm.pickup_track_number}
                  onChange={handleAssignChange}
                  placeholder="Номер накладной"
                  required
                />
              </label>
              <label>
                <span>Дата забора</span>
                <input
                  name="pickup_date"
                  type="date"
                  value={assignForm.pickup_date || ''}
                  onChange={handleAssignChange}
                  required
                />
              </label>
              <label
                className={`${styles.fileButton} ${isTtnDragActive ? styles.fileButtonActive : ''}`}
                onDragOver={(event) => {
                  event.preventDefault();
                  setIsTtnDragActive(true);
                }}
                onDragEnter={(event) => {
                  event.preventDefault();
                  setIsTtnDragActive(true);
                }}
                onDragLeave={() => setIsTtnDragActive(false)}
                onDrop={handleTtnDrop}
              >
                <Paperclip size={16} />
                <span>{ttnFile ? ttnFile.name : selectedRequest.pickup_ttn_file_url ? 'Заменить ТТН' : 'Прикрепить ТТН'}</span>
                <input
                  ref={ttnFileInputRef}
                  type="file"
                  onChange={(event) => setTtnFile(event.target.files?.[0] || null)}
                />
              </label>
              <button type="submit" className={styles.assignButton} disabled={isAssigning}>
                <Send size={16} />
                {isAssigning ? 'Назначаем...' : 'Назначить ТК'}
              </button>
            </form>

            <section className={styles.ticketBox}>
              <div className={styles.ticketBoxHeader}>
                <div>
                  <strong>Вопрос клиенту</strong>
                  <span>Создайте тикет, если по забору или документам нужно уточнение.</span>
                </div>
                <button type="button" className={styles.ticketToggle} onClick={() => setIsTicketFormOpen(prev => !prev)}>
                  <MessageSquare size={16} />
                  {isTicketFormOpen ? 'Скрыть' : 'Создать тикет'}
                </button>
              </div>
              {isTicketFormOpen && (
                <div className={styles.ticketForm}>
                  <textarea
                    value={ticketMessage}
                    onChange={(event) => setTicketMessage(event.target.value)}
                    rows={3}
                    placeholder="Напишите вопрос клиенту"
                  />
                  <button type="button" className={styles.assignButton} onClick={createPickupTicket} disabled={isTicketSending}>
                    <Send size={16} />
                    {isTicketSending ? 'Отправляем...' : 'Отправить вопрос'}
                  </button>
                </div>
              )}
            </section>

            <div className={styles.infoGrid}>
              <div>
                <span>Адрес забора</span>
                <strong>{selectedRequest.pickup_address || '-'}</strong>
              </div>
              <div>
                <span>Контактное лицо</span>
                <strong>{selectedRequest.pickup_contact_name || '-'}</strong>
              </div>
              <div>
                <span>Телефон</span>
                <strong>{selectedRequest.pickup_contact_phone || '-'}</strong>
              </div>
              <div>
                <span>Мест / вес</span>
                <strong>{selectedRequest.pickup_places_count || '-'} / {selectedRequest.pickup_weight_kg || '-'} кг</strong>
              </div>
              <div>
                <span>Дата заявки</span>
                <strong>{formatDate(selectedRequest.created_at)}</strong>
              </div>
              <div>
                <span>Оборудование</span>
                <strong>{selectedRequest.total_equipment || 0} ед.</strong>
              </div>
            </div>

            <div className={styles.fileLinks}>
              {selectedRequest.client_equipment_file_url && (
                <a href={selectedRequest.client_equipment_file_url} target="_blank" rel="noreferrer">
                  <Paperclip size={15} />
                  Файл оборудования клиента
                </a>
              )}
              {selectedRequest.pickup_ttn_file_url && (
                <a href={selectedRequest.pickup_ttn_file_url} target="_blank" rel="noreferrer">
                  <Paperclip size={15} />
                  ТТН для клиента
                </a>
              )}
            </div>

            <section className={styles.equipmentSection}>
              <h3>Оборудование</h3>
              <div className={styles.equipmentList}>
                {(selectedRequest.equipment_items || []).map(item => (
                  <article key={item.id} className={styles.equipmentCard}>
                    <div>
                      <strong>{item.serial_number}</strong>
                      <span>{item.equipment_model}</span>
                    </div>
                    <em>{item.public_status || item.location_status_display || '-'}</em>
                    <p>{item.declared_fault || 'Неисправность не указана'}</p>
                  </article>
                ))}
              </div>
            </section>

            <div className={styles.timelineHint}>
              <CalendarDays size={17} />
              После приемки оборудования клиент получит уведомление о поступлении партии.
            </div>
          </>
        )}
      </section>
    </div>
  );
};

export default ClientPickupRequestsPage;
